import { useEffect, useRef, useState } from 'react';

import {
  type AssetDelivery,
  type Content,
  CONTENT_KINDS,
  type ContentKind,
  contentPath,
  deleteContent,
  type Delivery,
  FUNNEL_STAGES,
  type FunnelStage,
  retryContentSetup,
  setPlaced,
  type StorageSettings,
  toneLabel,
} from '../api.ts';
import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import {
  attentionReason,
  describeError,
  isActive,
  isWorking,
  needsAttention,
  useAppData,
  useKeyChords,
} from '../data.tsx';
import { Poster, useDeliveries, usePosterUrls } from '../deliveries.tsx';
import { Icon } from '../Icon.tsx';
import { Link, navigate, replaceSearch, useLocation } from '../router.tsx';
import { StageProgress } from '../StageProgress.tsx';
import { since, startsIn } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import {
  Badge,
  EmptyState,
  Kbd,
  Loader,
  PageHeader,
  Segmented,
  Skeleton,
  StatusBadge,
  StatusDot,
} from '../ui.tsx';

/**
 * Every content, as a table or as a wall of pictures.
 *
 * The filters live in the URL rather than in component state, so a bookmarked
 * `/content?filter=attention` is a to-do list, the sidebar's hold clock can
 * link to exactly what it is about, and a reload lands on the same view. The
 * table/grid choice is in the URL for the same reason and one more: the two
 * are not the same page, they are two readings of it, and `?view=grid` is how
 * the Overview's strip links to the gallery (US-074). The default is the table
 * and the default is never written down, so `/content` stays `/content`.
 * rows come from the app-wide poll in `data.tsx`, which re-reads every three
 * seconds while the tab is visible — a content is moved along by the
 * production loop in another process, so looking again is the only way to see
 * it happen.
 */

type Filter = 'all' | 'active' | 'attention' | 'review' | 'delivered' | 'rejected';

const FILTERS: readonly { value: Filter; label: string; test: (content: Content) => boolean }[] = [
  { value: 'all', label: 'All', test: () => true },
  // Producing, held and queued, or Claude at work on the operator's feedback:
  // the work the machine is doing or is about to.
  { value: 'active', label: 'Active', test: isActive },
  { value: 'attention', label: 'Needs you', test: needsAttention },
  // The two ends of the line, each on its own: a version waiting to be
  // approved is a job for the operator, an approved one is a result.
  { value: 'review', label: 'In review', test: (content) => content.status === 'review' && !isWorking(content) },
  { value: 'delivered', label: 'Delivered', test: (content) => content.status === 'delivered' },
  // Delivered, then ruled out: kept apart from Delivered so neither list
  // passes one for the other.
  { value: 'rejected', label: 'Rejected', test: (content) => content.status === 'rejected' },
];

function isFilter(value: string | null): value is Filter {
  return FILTERS.some((filter) => filter.value === value);
}

function isKind(value: string | null): value is ContentKind {
  return CONTENT_KINDS.some((kind) => kind === value);
}

function isFunnelStage(value: string | null): value is FunnelStage {
  return FUNNEL_STAGES.some((stage) => stage === value);
}

/**
 * Whether a content has been published where it was meant to go. 'all' is the
 * default and stays out of the URL; the other two are `placed=yes|no`.
 */
type PlacedFilter = 'all' | 'no' | 'yes';

const PLACED_FILTERS: readonly {
  value: PlacedFilter;
  label: string;
  test: (content: Content) => boolean;
}[] = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'no', label: 'Not placed', test: (content) => !content.briefDetails.placed },
  { value: 'yes', label: 'Placed', test: (content) => content.briefDetails.placed },
];

function isPlacedFilter(value: string | null): value is PlacedFilter {
  return PLACED_FILTERS.some((candidate) => candidate.value === value);
}

/** How the list is drawn: rows with every fact, or pictures with the few. */
type View = 'table' | 'grid';

const VIEWS: readonly { value: View; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'grid', label: 'Grid' },
];

function isView(value: string | null): value is View {
  return VIEWS.some((candidate) => candidate.value === value);
}

export function Contents() {
  const { contents, repositories, error, refresh, setContent } = useAppData();
  const { search } = useLocation();
  const toast = useToast();
  const params = new URLSearchParams(search);
  const raw = params.get('filter');
  const filter: Filter = isFilter(raw) ? raw : 'all';
  const kindParam = params.get('kind');
  const kind: ContentKind | 'all' = isKind(kindParam) ? kindParam : 'all';
  const repositoryFilter = params.get('repository') ?? '';
  const funnelParam = params.get('funnel');
  const funnel: FunnelStage | 'all' = isFunnelStage(funnelParam) ? funnelParam : 'all';
  const placedParam = params.get('placed');
  const placed: PlacedFilter = isPlacedFilter(placedParam) ? placedParam : 'all';
  const viewParam = params.get('view');
  const view: View = isView(viewParam) ? viewParam : 'table';
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [busyId, setBusyId] = useState<string | null>(null);
  /** What the server is doing for `busyId`, for the row's `Loader`. */
  const [busyWait, setBusyWait] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Content | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Only the grid draws pictures, so only the grid asks for them: in the table
  // the hook is handed no contents and makes no request at all (US-074).
  const { deliveries } = useDeliveries(view === 'grid' ? contents : null);
  const posters = usePosterUrls(deliveries);
  const delivered = new Map((deliveries ?? []).map((one) => [one.contentId, one]));

  useKeyChords({
    n: () => navigate('/content/new'),
    '/': () => searchRef.current?.focus(),
  });

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(window.location.search);
    // 'all' is the default of the segmented filter and of the kind select,
    // 'table' of the view, and '' of the rest: none belongs in the URL, so a
    // default view has a clean address and the back button has nothing
    // pointless to walk through.
    if (value === '' || value === 'all' || (key === 'view' && value === 'table')) next.delete(key);
    else next.set(key, value);
    replaceSearch(next);
  };

  // The search box writes to the URL a beat after typing stops, so a filter
  // survives a reload without putting a history entry behind every keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setParam('q', query.trim());
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [query]);

  const act = (
    content: Content,
    label: string,
    wait: string,
    action: () => Promise<string>,
  ): void => {
    setBusyId(content.id);
    setBusyWait(wait);
    action()
      .then(async (message) => {
        await refresh();
        toast.ok(message);
      })
      .catch((cause: unknown) => {
        toast.error(`${label}: ${describeError(cause)}`);
      })
      .finally(() => {
        setBusyId(null);
        setBusyWait(null);
      });
  };

  const onRetrySetup = (content: Content): void => {
    act(content, 'Retry setup', 'Setting up the container and cloning the repositories…', async () => {
      const result = await retryContentSetup(content.id);
      if (result.setup !== null && !result.setup.ok) throw new Error(result.setup.message);
      return `${content.name} is cloned and ready to plan.`;
    });
  };

  // The reply replaces the row in the cached list, so the badge and the button
  // change at once instead of on the next poll.
  const onTogglePlaced = (content: Content): void => {
    const next = !content.briefDetails.placed;
    setBusyId(content.id);
    setPlaced(content.id, next)
      .then((updated) => {
        setContent(updated);
        toast.ok(next ? `${content.name} is marked placed.` : `${content.name} is no longer placed.`);
      })
      .catch((cause: unknown) => {
        toast.error(`${next ? 'Mark placed' : 'Unmark placed'}: ${describeError(cause)}`);
      })
      .finally(() => {
        setBusyId(null);
      });
  };

  const onDelete = (): void => {
    if (deleting === null) return;
    const content = deleting;
    setDeleting(null);
    act(content, 'Delete', 'Deleting the content and its container…', async () => {
      await deleteContent(content.id);
      return `Deleted ${content.name}. Nothing in any repository changed.`;
    });
  };

  const all = contents ?? [];
  const active = FILTERS.find((candidate) => candidate.value === filter) ?? FILTERS[0]!;
  const placedTest =
    PLACED_FILTERS.find((candidate) => candidate.value === placed) ?? PLACED_FILTERS[0]!;
  const needle = query.trim().toLowerCase();
  const visible = all.filter(
    (content) =>
      active.test(content) &&
      (kind === 'all' || content.kind === kind) &&
      (funnel === 'all' || content.briefDetails.funnelStages.includes(funnel)) &&
      placedTest.test(content) &&
      (repositoryFilter === '' ||
        content.repositories.some((link) => link.repositoryId === repositoryFilter)) &&
      (needle === '' ||
        content.name.toLowerCase().includes(needle) ||
        content.formats.some((format) => format.includes(needle)) ||
        content.repositories.some((link) => link.repositoryName.toLowerCase().includes(needle)) ||
        briefText(content).some((text) => text.toLowerCase().includes(needle))),
  );
  const usable = (repositories ?? []).filter((repository) => repository.keyConfigured);
  // Only the repositories some content actually draws on: a filter that can
  // only ever return nothing is worse than no filter.
  const used = new Map<string, string>();
  for (const content of all) {
    for (const link of content.repositories) used.set(link.repositoryId, link.repositoryName);
  }

  return (
    <div className="page">
      <PageHeader
        title="Content"
        subtitle="Everything produced and everything in production."
        actions={
          <Link
            className="button button--primary"
            href="/content/new"
            aria-disabled={repositories !== null && usable.length === 0}
            title="n"
          >
            <Icon name="plus" />
            New content
          </Link>
        }
      />

      {error !== null && (
        <div className="notice notice--error" role="alert">
          <Icon name="x-circle" />
          <div className="notice__body">Could not read contents: {error}</div>
        </div>
      )}

      {repositories !== null && usable.length === 0 && (
        <div className="notice notice--info">
          <Icon name="info" />
          <div className="notice__body">
            Content is made from a repository with a deploy key.{' '}
            <Link className="link" href="/repositories">
              Add one
            </Link>{' '}
            first.
          </div>
        </div>
      )}

      <div className="toolbar">
        <Segmented
          ariaLabel="Filter content"
          value={filter}
          onChange={(value) => {
            setParam('filter', value);
          }}
          options={FILTERS.map((candidate) => ({
            value: candidate.value,
            label: candidate.label,
            count: candidate.value === 'all' ? undefined : all.filter(candidate.test).length,
          }))}
        />
        <div className="toolbar__spacer" />
        <select
          className="field__input field__input--inline"
          value={kind}
          onChange={(event) => {
            setParam('kind', event.target.value);
          }}
          aria-label="Kind"
        >
          <option value="all">Video and images</option>
          {CONTENT_KINDS.map((candidate) => (
            <option key={candidate} value={candidate}>
              {candidate === 'video' ? 'Video' : 'Images'} (
              {all.filter((content) => content.kind === candidate).length})
            </option>
          ))}
        </select>
        <select
          className="field__input field__input--inline"
          value={funnel}
          onChange={(event) => {
            setParam('funnel', event.target.value);
          }}
          aria-label="Funnel"
        >
          <option value="all">All funnel stages</option>
          {FUNNEL_STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {FUNNEL_LABEL[stage]} (
              {all.filter((content) => content.briefDetails.funnelStages.includes(stage)).length})
            </option>
          ))}
        </select>
        <Segmented
          ariaLabel="Placed"
          value={placed}
          onChange={(value) => {
            setParam('placed', value);
          }}
          options={PLACED_FILTERS.map((candidate) => ({
            value: candidate.value,
            label: candidate.label,
          }))}
        />
        {used.size > 1 && (
          <select
            className="field__input field__input--inline"
            value={repositoryFilter}
            onChange={(event) => {
              setParam('repository', event.target.value);
            }}
            aria-label="Repository"
          >
            <option value="">All repositories</option>
            {[...used].map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        )}
        <label className="search">
          <Icon name="search" />
          <input
            ref={searchRef}
            className="search__input"
            type="search"
            placeholder="Filter by name, format, repository or brief"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            aria-label="Filter content by name"
          />
          <Kbd>/</Kbd>
        </label>
        <Segmented
          ariaLabel="How the content is shown"
          value={view}
          onChange={(value) => {
            setParam('view', value);
          }}
          options={VIEWS}
        />
      </div>

      {contents === null ? (
        <div className="panel">
          <div className="panel__body">
            <Skeleton lines={4} />
          </div>
        </div>
      ) : all.length === 0 ? (
        <EmptyState
          icon="rocket"
          title="No content yet"
          action={
            usable.length > 0 && (
              <Link className="button button--primary" href="/content/new">
                <Icon name="plus" />
                Create the first one
              </Link>
            )
          }
        >
          A content plans itself with Claude from your brief and your repositories, then produces
          one step at a time until there is a version to review.
        </EmptyState>
      ) : visible.length === 0 && filter === 'rejected' && !all.some(active.test) ? (
        // Nothing has been ruled out at all, so there is no other filter to clear.
        <EmptyState icon="filter" title="Nothing rejected">
          No rejected contents.
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState icon="filter" title="Nothing matches">
          No content matches these filters.{' '}
          <button
            type="button"
            className="link link--button"
            onClick={() => {
              setQuery('');
              replaceSearch(new URLSearchParams());
            }}
          >
            Clear filters
          </button>
        </EmptyState>
      ) : view === 'grid' ? (
        <ul className="content-grid">
          {visible.map((content) => (
            <ContentCard
              key={content.id}
              content={content}
              delivery={delivered.get(content.id) ?? null}
              poster={posters.get(content.id) ?? null}
            />
          ))}
        </ul>
      ) : (
        <div className="table-wrap table-wrap--cards panel">
          <table className="table table--contents table--cards">
            <thead>
              <tr>
                <th className="table__status">
                  <span className="visually-hidden">Status</span>
                </th>
                <th>Content</th>
                <th>Formats</th>
                <th>Repositories</th>
                <th>Steps</th>
                <th>Updated</th>
                <th className="table__actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((content) => (
                <ContentRow
                  key={content.id}
                  content={content}
                  busy={busyId === content.id}
                  wait={busyId === content.id ? busyWait : null}
                  onRetrySetup={() => {
                    onRetrySetup(content);
                  }}
                  onTogglePlaced={() => {
                    onTogglePlaced(content);
                  }}
                  onDelete={() => {
                    setDeleting(content);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={deleting === null ? '' : `Delete ${deleting.name}?`}
        confirmLabel="Delete content"
        danger
        onConfirm={onDelete}
        onCancel={() => {
          setDeleting(null);
        }}
      >
        {deleting !== null && <DeletionWarning content={deleting} />}
      </ConfirmDialog>
    </div>
  );
}

/**
 * One content as a picture (US-074).
 *
 * A card carries what can be read at a glance — the frame, the name, the
 * status and how far along it is — and nothing else: the repositories, the
 * formats and the actions are a row's job, and a grid that tries to be a table
 * with pictures is worse than either. Only a delivered content has a frame to
 * draw; everything else gets the shape of its kind and the word for where it
 * has got to, which is the honest thing to show for a render that does not
 * exist yet.
 */
function ContentCard({
  content,
  delivery,
  poster,
}: {
  readonly content: Content;
  readonly delivery: Delivery | null;
  readonly poster: AssetDelivery | null;
}) {
  const note = rowNote(content);
  const reason = attentionReason(content);
  return (
    <li className="content-grid__item">
      <article className={`content-card${reason !== null ? ' content-card--attention' : ''}`}>
        <Link
          className="content-card__link"
          href={contentPath(content.id)}
          aria-label={`Open ${content.name}`}
        >
          {delivery === null ? (
            <span className="content-card__frame content-card__frame--empty">
              <Icon name={content.kind === 'video' ? 'device-camera-video' : 'image'} />
              <span className="poster__note">{frameNote(content)}</span>
            </span>
          ) : (
            <Poster delivery={delivery} url={poster} className="content-card__frame" />
          )}
        </Link>
        <div className="content-card__body">
          <span className="content-card__title">
            <Link className="link link--strong" href={contentPath(content.id)}>
              {content.name}
            </Link>
            <StatusBadge status={content.status} />
          </span>
          <span className="content-card__meta">
            {content.kind === 'video' ? 'Video' : 'Images'}
            {content.formats.length > 0 && ` · ${content.formats.join(', ')}`}
            {' · '}
            {since(content.updatedAt)}
          </span>
          {note !== null && <span className="content-card__meta">{note}</span>}
          {reason !== null && <AttentionBadge content={content} reason={reason} />}
          <StageProgress
            content={content}
            planParses={listPlanParses(content)}
            compact
            label={`Stages of ${content.name}`}
          />
        </div>
      </article>
    </li>
  );
}

/** Why a card has no picture, in the words of where the content has got to. */
function frameNote(content: Content): string {
  if (content.currentVersion === 0) return 'nothing rendered yet';
  if (content.status === 'review') return `version ${String(content.currentVersion)} in review`;
  return `version ${String(content.currentVersion)}, not delivered`;
}

function ContentRow({
  content,
  busy,
  wait,
  onRetrySetup,
  onTogglePlaced,
  onDelete,
}: {
  readonly content: Content;
  readonly busy: boolean;
  /** The sentence for the wait on this row, while there is one. */
  readonly wait: string | null;
  readonly onRetrySetup: () => void;
  readonly onTogglePlaced: () => void;
  readonly onDelete: () => void;
}) {
  const { tones } = useAppData();
  const { funnelStages, placed } = content.briefDetails;
  const note = rowNote(content);
  const primary = content.repositories.find((link) => link.role === 'primary') ?? null;
  const others = content.repositories.filter((link) => link !== primary);
  const reason = attentionReason(content);
  return (
    <tr className={reason !== null ? 'table__row--attention' : undefined}>
      <td className="table__status table__cell--desktop">
        <StatusDot status={content.status} />
      </td>
      <td className="table__cell--lead">
        <div className="cell-stack">
          <span className="cell-stack__title">
            <Link className="link link--strong" href={contentPath(content.id)}>
              {content.name}
            </Link>
            <StatusBadge status={content.status} />
            {content.queuePosition !== null && (
              <span className="badge badge--wait">queued #{content.queuePosition}</span>
            )}
          </span>
          <span className="cell-stack__meta">
            {content.kind === 'video' ? 'Video' : 'Images'}
            {content.tone !== null && ` · ${toneLabel(tones, content.tone)}`}
            {note !== null && ` · ${note}`}
          </span>
          {reason !== null && <AttentionBadge content={content} reason={reason} />}
          {(funnelStages.length > 0 || placed) && (
            <span className="cell-stack__badges" aria-label="Funnel and placement">
              {funnelStages.map((stage) => (
                <Badge key={stage}>{FUNNEL_LABEL[stage]}</Badge>
              ))}
              {placed && <Badge tone="done">Placed</Badge>}
            </span>
          )}
          {wait !== null && <Loader label={wait} size="sm" />}
        </div>
      </td>
      <td className="table__tags" data-label="Formats" title={content.formats.join(', ')}>
        {content.formats.length === 0 ? <span className="muted">—</span> : content.formats.join(', ')}
      </td>
      <td
        className="table__tags"
        data-label="Repositories"
        title={content.repositories.map((link) => `${link.repositoryName} (${link.role})`).join(', ')}
      >
        {primary === null ? (
          <span className="muted">none</span>
        ) : (
          <>
            {primary.repositoryName}
            {others.length > 0 && <span className="muted"> +{others.length}</span>}
          </>
        )}
      </td>
      <td className="table__progress" data-label="Stages">
        <StageProgress
          content={content}
          planParses={listPlanParses(content)}
          compact
          label={`Stages of ${content.name}`}
        />
      </td>
      <td className="table__time" data-label="Updated" title={content.updatedAt}>
        {since(content.updatedAt)}
      </td>
      <td className="table__actions table__cell--foot">
        <div className="row__actions">
          {canRetrySetup(content) && (
            <button
              type="button"
              className="button button--small"
              onClick={onRetrySetup}
              disabled={busy}
              title="Clone the repositories again, around the same workspace"
            >
              <Icon name="sync" />
              {busy ? 'Working…' : 'Retry setup'}
            </button>
          )}
          {content.status === 'delivered' && (
            <button
              type="button"
              className="button button--small"
              onClick={onTogglePlaced}
              disabled={busy}
              title={placed ? 'It is not published after all' : 'It is published where it was meant to go'}
            >
              <Icon name={placed ? 'x' : 'check'} />
              {placed ? 'Unmark placed' : 'Mark placed'}
            </button>
          )}
          <Link className="button button--small button--quiet" href={contentPath(content.id)}>
            {content.status === 'pending' ? 'Plan' : 'Open'}
            <Icon name="chevron-right" />
          </Link>
          <button
            type="button"
            className="button button--small button--quiet button--danger button--icon"
            onClick={onDelete}
            disabled={busy}
            aria-label={`Delete ${content.name}`}
            title="Delete"
          >
            <Icon name="trash" />
          </button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Setup is offered while the plan has not been written — the clone is what the
 * planning conversation reads — and on a content that failed at setup. It is
 * safe to run more than once: a clone that succeeded is reused, not repeated.
 */
function canRetrySetup(content: Content): boolean {
  if (content.status === 'pending') return true;
  return content.status === 'failed' && content.failureStage === 'setup';
}

/** The brief-detail texts the search box also reads. */
function briefText(content: Content): string[] {
  const { goal, audience, angle, placement } = content.briefDetails;
  return [goal, audience, angle, placement].filter((text): text is string => text !== null);
}

/** The one thing worth saying under the name, if anything. */
function rowNote(content: Content): string | null {
  // Why the content needs the operator is its badge; the note is for the rest.
  if (needsAttention(content)) return null;
  if (content.revising) return 'making your changes';
  if (content.status === 'approval' && content.sketchState === 'adjusting') return 'adjusting the scenes';
  if (content.status === 'approval' && content.sketchState === 'preparing') return 'preparing the scenes';
  if (content.scheduledStartAt !== null) return startsIn(content.scheduledStartAt);
  if (content.status === 'pending') return 'planning';
  return null;
}

/**
 * What the content needs from the operator, in the overview's words. A failure
 * is `danger`; everything else that waits on a person is `wait`.
 */
function AttentionBadge({ content, reason }: { readonly content: Content; readonly reason: string }) {
  // Wrapped, so the column it sits in does not stretch the badge to its width.
  return (
    <span>
      <Badge tone={content.status === 'failed' ? 'danger' : 'wait'}>{reason}</Badge>
    </span>
  );
}

/**
 * The list knows whether a pending plan parses, so the bar's "needs you" and
 * the badge agree; past pending the bar reads the plan off the step count.
 */
function listPlanParses(content: Content): boolean | null {
  return content.status === 'pending' ? content.planParses : null;
}

/**
 * What the confirmation has to say before anything is removed: exactly what
 * goes, exactly what stays, whether the bucket keeps its copies, and — for a
 * content mid-production — that its agent is stopped first.
 *
 * The bucket sentence states which of the two things is about to happen
 * rather than naming the setting (US-057). "Keep copies on delete is on" asks
 * the operator to remember what that means with their hand on a red button;
 * "the objects already in the bucket stay there" is the answer itself.
 */
export function DeletionWarning({ content }: { readonly content: Content }) {
  const { storage } = useAppData();
  return (
    <>
      <p>
        Its container and its workspace on this server are removed: the clones, the plan, every
        asset and every render that was not delivered.
      </p>
      <BucketWarning storage={storage} />
      <p>
        The repositories it reads are clones and are never written to, so nothing on any remote
        changes.
      </p>
      {content.status === 'producing' && (
        <p>
          <strong>This content is producing.</strong> The loop is stopped and its agent ended
          gracefully first, which can take a moment.
        </p>
      )}
      {content.status === 'waiting' && (
        <p>
          <strong>This content is mid-production,</strong> held until it can resume. Deleting it is
          deleting a production in progress: it will not come back when the hold lifts.
        </p>
      )}
      {content.versionCount > 0 && (
        <p>
          {content.versionCount === 1
            ? 'Its one rendered version goes with it.'
            : `All ${String(content.versionCount)} of its rendered versions go with it.`}
        </p>
      )}
    </>
  );
}

/**
 * What happens in the bucket, in the words of what happens — never in the
 * words of the setting. The prefix is named in the deleting case because that
 * is the one an operator may want to check afterwards.
 */
function BucketWarning({ storage }: { readonly storage: StorageSettings | null }) {
  if (storage === null) return <p className="muted">Checking what happens in the bucket…</p>;
  if (!storage.configured) {
    return (
      <p>
        No bucket is configured, so nothing is delivered anywhere and there is nothing to remove
        from object storage.
      </p>
    );
  }
  if (storage.keepCopiesOnDelete) {
    return (
      <p>
        <strong>Its delivered files stay in {storage.bucket}.</strong> Everything already uploaded
        is left exactly where it is, so any published link keeps working. Removing them is then
        yours to do in the bucket.
      </p>
    );
  }
  return (
    <p>
      <strong>Its delivered files are removed from {storage.bucket}.</strong> Every object under{' '}
      <code>
        {storage.keyPrefix}contents/&lt;id&gt;/
      </code>{' '}
      goes, including delivered renders — any published link to them stops working. No other
      content’s files are touched.
    </p>
  );
}
